import { getNoticeByIdAPI } from '@/src/apis/rest/notice';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DetailSection from '@/src/components/organism/DetailSection';
import { NAV_CONST } from '@/src/constants/nav';
import dayjs from 'dayjs';
import PhonePreview from '@/src/components/molecule/PhonePreview';
import ContentsPreview from '@/src/components/molecule/ContentsPreview';
import { OPTIONS_CONST } from '@/src/constants/options';

const BACK_PATH = PATH.NOTICE_LIST;
const EDIT_PATH = PATH.NOTICE_EDIT;

export default function NoticeDetailPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();

  // query
  const { data: noticeData } = useQuery({
    queryKey: ['notice', id],
    queryFn: () => getNoticeByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  // event
  const handleGoBack = () => {
    navigate(-1);
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonColor="outline"
            buttonSize="medium"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Link to={EDIT_PATH.replace(':id', id || '')}>
            <Button className="w-40" buttonSize="medium">
              {BUTTON_CONST.editShort}
            </Button>
          </Link>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.notice, NAV_CONST.noticeDetail]}
        pageTitle={NAV_CONST.noticeDetail}
      />
      <div className="mb-[100px] flex flex-col gap-4">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                공지사항 내용
              </span>
            </div>
          }
          data={noticeData}
          contents={[
            [
              {
                key: 'categoryType',
                lable: '유형',
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.commonCategory,
                      key: 'common',
                      badgeColor: 'lightBlue',
                    },
                    {
                      label: OPTIONS_CONST.communityCategory,
                      key: 'community',
                      badgeColor: 'lightYellow',
                    },
                  ],
                }),
              },
              {
                key: 'showedYn',
                lable: '노출 여부',
                valueSetter: data => (data?.showedYn ? 'true' : 'false'),
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.showed,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notShowed,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'fixedTopYn',
                lable: '우선 노출 여부',
                valueSetter: data => (data?.fixedTopYn ? 'true' : 'false'),
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.applied,
                      key: 'true',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.notApplied,
                      key: 'false',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'viewCount',
                lable: '조회 수',
                valueSetter: data => {
                  return DetailSctionRenderer.NUMBER()(
                    typeof data?.viewCount === 'number'
                      ? String(data?.viewCount)
                      : '0',
                  );
                },
                render: DetailSctionRenderer.PREFIX_SUFFIX({ suffix: '회' }),
              },
            ],
            [
              {
                key: 'createdAt',
                lable: '등록 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: ['administrator', 'name'],
                lable: '등록 관리자',
              },
            ],
          ]}
          bottomContents={
            <div className="flex gap-6">
              <ContentsPreview
                title={noticeData?.title}
                contents={noticeData?.contents}
              />
              <div className="w-[400px] flex-shrink-0">
                <PhonePreview>
                  <div className="sticky top-0">
                    <img
                      src="/assets/images/img-notice-header-preview.png"
                      alt="공지사항"
                      width="100%"
                      height="auto"
                    />
                    <PhonePreview.Header className="flex flex-col gap-1 bg-white">
                      <h3 className="break-keep text-h20sb">
                        {noticeData?.title || '제목을 입니다'}
                      </h3>
                      <p className="text-b14r text-gray400">
                        {dayjs().format('YYYY.MM.DD HH:mm')}
                      </p>
                    </PhonePreview.Header>
                  </div>
                  <PhonePreview.Content>
                    <div
                      className="ck-content"
                      dangerouslySetInnerHTML={{
                        __html: noticeData?.contents || '내용 입니다',
                      }}
                    />
                  </PhonePreview.Content>
                </PhonePreview>
              </div>
            </div>
          }
        />
      </div>
      {renderBottomFixedContent()}
    </>
  );
}
